import { create } from 'zustand';
import { TOKEN_KEY, tokenStorage } from './storage';
import { createSessionQueryClient } from './queryClient';
import { clearSessionLocalState } from './sessionLocalState';

export type Clinician = {
  id?: number;
  hospital_ids?: number[];
  hospital_id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  flags?: Record<string, unknown>;
  locale?: string;
  /** Internal RxFood staff flag. Drives visibility of admin-only UI like
   *  the patient-list "Draft report available" chip — v1 gates that chip
   *  on the same flag (clinician/src/pages/patientlist.tsx:268). */
  is_internal?: boolean;
  [k: string]: unknown;
};

type State = {
  token: string | null;
  queryClient: ReturnType<typeof createSessionQueryClient>;
  sessionVersion: number;
  clinician: Clinician | null;
  pendingCredentials: { username: string; password: string } | null;
  setToken: (token: string | null) => void;
  setClinician: (clinician: Clinician | null) => void;
  setPendingCredentials: (creds: { username: string; password: string } | null) => void;
  logout: () => Promise<void>;
};

const FLAG_OVERRIDE_KEY = 'dev.flagOverrides';

/**
 * Read dev-only flag overrides from localStorage. Production builds skip
 * this entirely (see `import.meta.env.DEV` gate). The overrides are merged
 * on top of whatever flags the server returns, so a clinician with
 * `clinician_allow_meal_edit: false` from the API can still test the
 * meal-edit UI locally.
 */
const readFlagOverrides = (): Record<string, unknown> => {
  if (!import.meta.env.DEV) return {};
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(FLAG_OVERRIDE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const applyFlagOverrides = (clinician: Clinician | null): Clinician | null => {
  if (!clinician) return clinician;
  const overrides = readFlagOverrides();
  if (Object.keys(overrides).length === 0) return clinician;
  return {
    ...clinician,
    flags: {
      ...((clinician.flags as Record<string, unknown> | undefined) ?? {}),
      ...overrides,
    },
  };
};

export const useAuthStore = create<State>((set, get) => ({
  token: tokenStorage.read(),
  queryClient: createSessionQueryClient(),
  sessionVersion: 0,
  clinician: null,
  pendingCredentials: null,

  setToken: (token) => {
    if (token) tokenStorage.write(token);
    else tokenStorage.clear();
    if (token === get().token) return;
    // clear() cancels queries and removes both query and mutation state.
    // A new client also isolates late mutation callbacks that captured the old one.
    const { queryClient, sessionVersion } = get();
    queryClient.clear();
    clearSessionLocalState();
    set({
      token,
      clinician: null,
      pendingCredentials: null,
      queryClient: createSessionQueryClient(),
      sessionVersion: sessionVersion + 1,
    });
  },

  setClinician: (clinician) => set({ clinician: applyFlagOverrides(clinician) }),

  setPendingCredentials: (creds) => set({ pendingCredentials: creds }),

  logout: async () => {
    const token = get().token;
    // End the local session immediately, even if revocation is slow or fails.
    get().setToken(null);
    try {
      const { authApi } = await import('./api');
      if (token) await authApi.logout(token).catch(() => undefined);
    } catch {
      // ignore
    }
  },
}));

// Another tab changed the token. Adopt it so this tab drops the old session's cache.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== null && e.key !== TOKEN_KEY) return;
    useAuthStore.getState().setToken(e.newValue);
  });
}
